import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IPaginatedReactQueryParams } from '../interfaces';
import {
  createActivityLog,
  createPlannedActivity,
  deleteActivityLog,
  deletePlannedActivity,
  getActions,
  getActivityLog,
  getActivityLogs,
  getPlannedActivities,
  getPlannedActivity,
  getPlannedActivityWorkLog,
  resolvePlannedActivity,
  updateActivityLog,
  updatePlannedActivity,
} from '../../api/activitiesApi';
import {
  Action,
  ActivitiesSort,
  ActivityLogDetails,
  ICreateActivityLogParams,
  ICreatePlannedActivityParams,
  IResolveParams,
  PlannedActivitiesSort,
  PlannedActivityDetails,
} from '../../api/activitiesApi/interfaces';
import { AxiosError } from 'axios';
import { IPaginatedWorkLogReactQueryParams } from './interfaces';

export const useGetActivityLogs = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<ActivitiesSort>) => {
  return useQuery({
    queryKey: ['activity-logs', page, pageSize, sortField, search, filter],
    queryFn: () => {
      return getActivityLogs(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetActivityLog = (id: number) => {
  return useQuery<ActivityLogDetails>({
    queryFn: () => getActivityLog(id),
    queryKey: ['activity-log', id],
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreatActivityLogtMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, ICreateActivityLogParams>({
    mutationFn: createActivityLog,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useUpdateActivityLogMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    { activityLogId: number; activityLogData: ICreateActivityLogParams }
  >({
    mutationFn: async ({ activityLogId, activityLogData }) =>
      updateActivityLog(activityLogId, activityLogData),
    onSuccess: (activityLogId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['activity-log', activityLogId],
      });
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useGetActions = (enabled: boolean = true) => {
  return useQuery<Action[]>({
    queryFn: () => getActions(),
    queryKey: ['actions'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useDeleteActivityLog = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deleteActivityLog,
    onSuccess: (_, activityLogId) => {
      queryClient.removeQueries(['activity-log', activityLogId]);
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useGetPlannedActivities = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<PlannedActivitiesSort>) => {
  return useQuery({
    queryKey: ['planned-activities', page, pageSize, sortField, search, filter],
    queryFn: () => {
      return getPlannedActivities(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetPlannedActivity = (id: number) => {
  return useQuery<PlannedActivityDetails>({
    queryFn: () => getPlannedActivity(id),
    queryKey: ['planned-activity', id],
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreatePlannedActivityMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, ICreatePlannedActivityParams>({
    mutationFn: createPlannedActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useUpdatePlannedActivityMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    {
      plannedActivityId: number;
      plannedActivityData: ICreatePlannedActivityParams;
    }
  >({
    mutationFn: async ({ plannedActivityId, plannedActivityData }) =>
      updatePlannedActivity(plannedActivityId, plannedActivityData),
    onSuccess: (plannedActivityId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['planned-activity', plannedActivityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useResolvePlannedActivityMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    {
      plannedActivityId: number;
      resolveData: IResolveParams;
    }
  >({
    mutationFn: async ({ plannedActivityId, resolveData }) =>
      resolvePlannedActivity(plannedActivityId, resolveData),
    onSuccess: (plannedActivityId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['planned-activity', plannedActivityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['activity-logs'],
      });
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useDeletePlannedActivity = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deletePlannedActivity,
    onSuccess: (_, plannedActivityId) => {
      queryClient.removeQueries(['planned-activity', plannedActivityId]);
      queryClient.invalidateQueries({
        queryKey: ['actions'],
      });
    },
  });
};

export const useGetPlannedActivityWorkLog = ({
  id,
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedWorkLogReactQueryParams) => {
  return useQuery({
    queryKey: [
      'planned-activity-work-log',
      id,
      page,
      pageSize,
      sortField,
      search,
      filter,
    ],
    queryFn: () => {
      return getPlannedActivityWorkLog(
        id,
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
